import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import * as XLSX from 'xlsx';

const LONG_PRESS_DURATION_MS = 600;

@Component({
  selector: 'app-common-search',
  templateUrl: './common-component-search.component.html',
  styleUrls: ['./common-component-search.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIcon,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
  ],
})
export class CommonSearchComponent implements OnInit {
  @Input() initialSearchValue = ''; // Input for initial value
  @Input() placeHolder = '';
  // Not every page needs bulk search - keep it opt-in per usage
  @Input() enableBulkSearch = false;
  @Input() bulkSearchSampleFileUrl =
    '../../assets/downloadableSampleFiles/Sample_Bulk_Search_Sheet.xlsx';
  @Input() bulkSearchSampleFileName = 'Sample_Bulk_Search_Sheet.xlsx';

  @Output() searchSubmit = new EventEmitter<string>();
  @Output() cleared = new EventEmitter<void>();
  // Emits the search values parsed from the uploaded sheet, joined into a single comma-separated string
  @Output() bulkSearchSubmit = new EventEmitter<string>();

  searchForm: FormGroup = this.formBuilder.group({});

  showBulkSearchModal = false;
  bulkSearchFileName = '';
  bulkSearchValues: string[] = [];
  bulkSearchError = '';

  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private longPressTriggered = false;

  constructor(private formBuilder: FormBuilder) {
    this.searchForm = this.formBuilder.group({
      searchInput: [''],
    });
  }

  ngOnInit(): void {
    // Set the initial value for the search box
    this.searchForm.get('searchInput')?.setValue(this.initialSearchValue);
  }

  onSearch(event): void {
    this.performSearch();
    event.preventDefault();
  }

  onSearchClick(): void {
    // A long press that opened the bulk search popup shouldn't also fire a normal search
    if (this.longPressTriggered) {
      this.longPressTriggered = false;
      return;
    }
    this.performSearch();
  }

  private performSearch(): void {
    const searchTerm = this.searchForm.get('searchInput')?.value.trim();
    if (searchTerm) {
      this.searchSubmit.emit(searchTerm);
    } else {
      this.searchSubmit.emit('');
    }
  }

  // Long-press (or long touch-hold) on the search button opens the bulk upload popup
  onSearchBtnPressStart(): void {
    if (!this.enableBulkSearch) {
      return;
    }
    this.clearLongPressTimer();
    this.longPressTimer = setTimeout(() => {
      this.longPressTriggered = true;
      this.openBulkSearchModal();
    }, LONG_PRESS_DURATION_MS);
  }

  onSearchBtnPressEnd(): void {
    this.clearLongPressTimer();
  }

  private clearLongPressTimer(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  openBulkSearchModal(): void {
    this.showBulkSearchModal = true;
    this.bulkSearchFileName = '';
    this.bulkSearchValues = [];
    this.bulkSearchError = '';
  }

  closeBulkSearchModal(): void {
    this.showBulkSearchModal = false;
  }

  downloadBulkSearchSampleFile(): void {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', this.bulkSearchSampleFileUrl);
    link.setAttribute('download', this.bulkSearchSampleFileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  onBulkFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }

    this.bulkSearchFileName = file.name;
    this.bulkSearchError = '';
    this.bulkSearchValues = [];

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const workBook = XLSX.read(reader.result, { type: 'binary' });
        const firstSheet = workBook.Sheets[workBook.SheetNames[0]];
        const rows: unknown[][] = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          defval: '',
        });

        const parsedValues = rows
          .slice(1) // first row is the header
          .map((row) => (row?.[0] ?? '').toString().trim())
          .filter((value) => !!value);
        this.bulkSearchValues = Array.from(new Set(parsedValues));

        if (this.bulkSearchValues.length === 0) {
          this.bulkSearchError =
            'No search values found. The first row should be a header and values should be in the first column.';
        }
      } catch {
        this.bulkSearchError =
          'Unable to read this file. Please upload a valid CSV/Excel sheet.';
      }
    };
    reader.readAsBinaryString(file);

    // Allow selecting the same file again later
    input.value = '';
  }

  onBulkSearchSubmit(): void {
    if (this.bulkSearchValues.length === 0) {
      return;
    }
    const combinedSearchValue = this.bulkSearchValues.join(',');
    this.searchForm.get('searchInput')?.setValue(combinedSearchValue);
    this.bulkSearchSubmit.emit(combinedSearchValue);
    this.closeBulkSearchModal();
  }
}
